import { Box, CircularProgress, Typography } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { pendingQuestionIndex } from '../../helpers'
import type { CaStreaming, Turn } from '../../shared/types'
import AssistantTurn from './AssistantTurn'
import EmptyState, { EmptyStateAgent } from './EmptyState'
import ThreadSkeleton from './ThreadSkeleton'
import UserBubble from './UserBubble'

function hasContent(t: Turn): boolean {
  return Boolean(t.text) || (t.tables?.length ?? 0) > 0 || (t.charts?.length ?? 0) > 0
}

export default function MessageThread(
  { turns, streaming, loading, creating, agent, onStarter }:
  {
    turns: Turn[]
    streaming?: CaStreaming | null
    loading?: boolean
    creating?: boolean
    agent?: EmptyStateAgent
    onStarter?: (q: string) => void
  },
) {
  const intl = useIntl()
  const bottomRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ block: 'end' })
  }, [turns.length, streaming?.text, streaming?.thinking, streaming?.thought, streaming?.thoughts?.length])

  if (loading) {
    return <ThreadSkeleton />
  }
  if (creating) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          color: 'text.secondary',
        }}
      >
        <CircularProgress size={22} />
        <Typography variant="body2">{intl.formatMessage({ id: 'caChat.thread.creating' })}</Typography>
      </Box>
    )
  }
  const hasVisibleTurn = turns.some(hasContent)
  if (!hasVisibleTurn && !streaming) {
    return <EmptyState agent={agent} onStarter={onStarter} />
  }
  const shown: Turn[] = streaming
    ? [
        ...turns.slice(0, pendingQuestionIndex(turns, streaming.question, streaming.turnsBefore)),
        { role: 'user', text: streaming.question, createdAt: '' },
        { role: 'assistant', text: streaming.text, thoughts: streaming.thoughts, createdAt: '' },
      ]
    : turns
  const pendingIndex = streaming ? shown.length - 1 : -1

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {shown.map((t, i) => {
        const pending = i === pendingIndex
        if (!pending && !hasContent(t) && !(t.thoughts?.length)) { return null }
        if (t.role === 'user') {
          return <UserBubble key={i} text={t.text ?? ''} />
        }
        return (
          <AssistantTurn
            key={i}
            turn={t}
            pending={pending ? { thought: streaming?.thought } : undefined}
          />
        )
      })}
      <div ref={bottomRef} />
    </Box>
  )
}
